import React from 'react';
import { IonIcon } from '@ionic/react';
import { chevronForwardOutline } from 'ionicons/icons';
import './ProListRow.css';

export type ProListRowTone = 'default' | 'danger';

export interface ProListRowProps {
  /** Ionicon name for the leading icon-circle. Omit for icon-less rows (e.g. toggle rows). */
  icon?: string;
  title: React.ReactNode;
  /** Optional second line below the title. */
  note?: React.ReactNode;
  /** Makes the row interactive. When set, the row renders as <button> and a chevron is shown by default. */
  onClick?: () => void;
  /**
   * Right-side content. When `onClick` is set, defaults to a forward chevron;
   * pass any node to override (e.g. <IonToggle/>, custom label).
   * Pass `null` to suppress the default chevron without supplying a replacement.
   */
  trailing?: React.ReactNode;
  tone?: ProListRowTone;
  className?: string;
}

const classes = (...parts: Array<string | false | undefined>) =>
  parts.filter(Boolean).join(' ');

const ProListRow: React.FC<ProListRowProps> = ({
  icon,
  title,
  note,
  onClick,
  trailing,
  tone = 'default',
  className,
}) => {
  const isInteractive = typeof onClick === 'function';
  const rootClass = classes(
    'pro-list-row',
    isInteractive && 'pro-list-row--interactive',
    tone === 'danger' && 'pro-list-row--danger',
    className,
  );

  const resolvedTrailing =
    trailing === undefined && isInteractive ? (
      <IonIcon icon={chevronForwardOutline} />
    ) : (
      trailing
    );

  const content = (
    <>
      {icon && (
        <span className="pro-list-row__icon">
          <IonIcon icon={icon} />
        </span>
      )}
      <span className="pro-list-row__body">
        <span className="pro-list-row__title">{title}</span>
        {note && <span className="pro-list-row__note">{note}</span>}
      </span>
      {resolvedTrailing != null && (
        <span className="pro-list-row__trailing">{resolvedTrailing}</span>
      )}
    </>
  );

  if (isInteractive) {
    return (
      <button type="button" className={rootClass} onClick={onClick}>
        {content}
      </button>
    );
  }

  return <div className={rootClass}>{content}</div>;
};

export default ProListRow;
